import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Download, FileSpreadsheet, RefreshCw } from "lucide-react";
import { ADMIN_ROLES, VALIDATION_STATUSES } from "@academichub/shared";
import { ApiError } from "../api/client";
import { downloadReportCsv, getReport } from "../api/reports";
import { listCategories, listSemesters } from "../api/master";
import { useAuth } from "../hooks/useAuth";
import { formatDate, validationStatusLabel } from "../lib/format";
import {
  Alert,
  Button,
  Card,
  EmptyState,
  Field,
  Input,
  PageHeader,
  Select,
  Spinner,
  StatusBadge,
  ValidationBadge,
} from "../components/ui";

export function ReportsPage() {
  const { user } = useAuth();
  const isAdmin = user ? ADMIN_ROLES.includes(user.role) : false;

  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [semesterId, setSemesterId] = useState("");
  const [validationStatus, setValidationStatus] = useState("");
  const [error, setError] = useState<string | null>(null);

  const filters = {
    from: from ? new Date(`${from}T00:00:00.000Z`).toISOString() : "",
    to: to ? new Date(`${to}T23:59:59.999Z`).toISOString() : "",
    categoryId,
    semesterId,
    validationStatus,
  };

  const categoriesQuery = useQuery({ queryKey: ["master", "categories"], queryFn: listCategories });
  const semestersQuery = useQuery({ queryKey: ["master", "semesters"], queryFn: listSemesters });

  const reportQuery = useQuery({
    queryKey: ["reports", filters],
    queryFn: () => getReport(filters),
  });

  const csvMutation = useMutation({
    mutationFn: () => downloadReportCsv(filters),
    onSuccess: () => setError(null),
    onError: (caught) => {
      setError(caught instanceof ApiError ? caught.message : "Ekspor CSV gagal.");
    },
  });

  const rows = reportQuery.data?.items ?? [];

  return (
    <div className="space-y-5">
      <PageHeader
        title="Laporan kegiatan"
        description="Rekap kegiatan dalam lingkup wewenang Anda, siap diunduh sebagai CSV."
        actions={
          <>
            <Button
              variant="secondary"
              onClick={() => void reportQuery.refetch()}
              loading={reportQuery.isFetching}
            >
              <RefreshCw className="size-4" aria-hidden />
              Muat ulang
            </Button>
            <Button loading={csvMutation.isPending} onClick={() => csvMutation.mutate()}>
              <Download className="size-4" aria-hidden />
              Unduh CSV
            </Button>
          </>
        }
      />

      {error ? <Alert tone="error">{error}</Alert> : null}

      <Card>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
          <Field label="Dari tanggal" htmlFor="from">
            <Input
              id="from"
              type="date"
              value={from}
              onChange={(event) => setFrom(event.target.value)}
            />
          </Field>
          <Field label="Sampai tanggal" htmlFor="to">
            <Input id="to" type="date" value={to} onChange={(event) => setTo(event.target.value)} />
          </Field>
          <Field label="Kategori" htmlFor="reportCategory">
            <Select
              id="reportCategory"
              value={categoryId}
              onChange={(event) => setCategoryId(event.target.value)}
            >
              <option value="">Semua kategori</option>
              {(categoriesQuery.data?.items ?? []).map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Semester" htmlFor="reportSemester">
            <Select
              id="reportSemester"
              value={semesterId}
              onChange={(event) => setSemesterId(event.target.value)}
            >
              <option value="">Semua semester</option>
              {(semestersQuery.data?.items ?? []).map((item) => (
                <option key={item.id} value={item.id}>
                  {item.label}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Status validasi" htmlFor="reportValidation">
            <Select
              id="reportValidation"
              value={validationStatus}
              onChange={(event) => setValidationStatus(event.target.value)}
            >
              <option value="">Semua validasi</option>
              {VALIDATION_STATUSES.map((value) => (
                <option key={value} value={value}>
                  {validationStatusLabel(value)}
                </option>
              ))}
            </Select>
          </Field>
        </div>
      </Card>

      {reportQuery.isLoading ? <Spinner label="Menyusun laporan..." /> : null}

      {reportQuery.data ? (
        <Card className="space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="flex items-center gap-2 text-sm text-slate-600">
              <FileSpreadsheet className="size-4 text-brand-600" aria-hidden />
              {reportQuery.data.total} kegiatan pada lingkup Anda
              {reportQuery.data.truncated ? " (ditampilkan sebagian)" : ""}.
            </p>
          </div>

          {rows.length === 0 ? (
            <EmptyState
              title="Tidak ada data"
              description="Ubah rentang tanggal atau filter lainnya."
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-slate-200 text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wide text-slate-500">
                    <th className="px-2 py-2">Kode</th>
                    <th className="px-2 py-2">Judul</th>
                    <th className="px-2 py-2">Tanggal</th>
                    <th className="px-2 py-2">Kategori</th>
                    <th className="px-2 py-2">Prodi</th>
                    <th className="px-2 py-2">Status</th>
                    <th className="px-2 py-2">Validasi</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {rows.map((row) => (
                    <tr key={row.id} className="hover:bg-slate-50">
                      <td className="whitespace-nowrap px-2 py-2 font-mono text-xs text-slate-500">
                        {row.activityCode}
                      </td>
                      <td className="px-2 py-2">
                        <Link
                          to={`/kegiatan/${row.id}`}
                          className="font-medium text-slate-800 hover:underline"
                        >
                          {row.title}
                        </Link>
                      </td>
                      <td className="whitespace-nowrap px-2 py-2 text-slate-600">
                        {formatDate(row.activityDate)}
                      </td>
                      <td className="px-2 py-2 text-slate-600">{row.category.name}</td>
                      <td className="px-2 py-2 text-slate-600">{row.studyProgram.name}</td>
                      <td className="px-2 py-2">
                        <StatusBadge status={row.status} />
                      </td>
                      <td className="px-2 py-2">
                        <ValidationBadge status={row.validationStatus} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      ) : null}

      {!isAdmin ? (
        <p className="text-xs text-slate-500">
          Laporan hanya memuat kegiatan yang boleh Anda lihat. Hubungi admin bila data tidak sesuai.
        </p>
      ) : null}
    </div>
  );
}
